import { Message, StoryOption, WinCondition, GameOutcome, StoryResponse, Difficulty, AdventurePlan, GameMode, Genre, RewindPenalty } from '../types';

// API base URL - works both locally (with vercel dev) and on Vercel
const API_BASE = ''; // Empty string works for both cases - relative URLs

// Error message constants
const STREAM_ERROR_MESSAGE = "The narrator falls silent. (Stream Error)";
const NON_STREAM_ERROR_MESSAGE = "The narrator falls asleep. (AI Error)";
const TIMEOUT_MS = 30000; // 30 second timeout for API calls
const MAX_RETRIES = 2; // Number of retries for stream errors

// Helper to create fetch with timeout
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number = TIMEOUT_MS): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout - please try again');
    }
    throw error;
  }
}

// Helper function to handle streaming responses with outcome support
async function handleStreamResponse(
  response: Response, 
  onChunk?: (chunk: string) => void,
  onThinkingChange?: (isThinking: boolean) => void
): Promise<StoryResponse> {
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  let fullText = '';
  let outcome: GameOutcome = null;
  let outcomeReason: string | undefined;
  let countdown: StoryResponse['countdown'];

  if (!reader) {
    throw new Error('No response body reader available');
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n').filter(line => line.trim() !== '');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          
          if (data === '[DONE]') {
            return { 
              text: fullText || STREAM_ERROR_MESSAGE,
              outcome,
              outcomeReason,
              countdown
            };
          }

          try {
            const parsed = JSON.parse(data);
            // Handle thinking state
            if (parsed.thinking !== undefined && onThinkingChange) {
              onThinkingChange(parsed.thinking);
            }
            if (parsed.content) {
              fullText += parsed.content;
              if (onChunk) {
                onChunk(parsed.content);
              }
            }
            // Handle outcome from tool calls
            if (parsed.outcome) {
              outcome = parsed.outcome as GameOutcome;
              outcomeReason = parsed.outcomeReason;
            }
            // Handle countdown updates
            if (parsed.countdown) {
              countdown = parsed.countdown;
            }
          } catch (e) {
            // Skip malformed chunks
            console.error('Error parsing stream chunk:', e);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error reading stream:', error);
  }

  return { 
    text: fullText || STREAM_ERROR_MESSAGE,
    outcome,
    outcomeReason,
    countdown
  };
}

export async function startStory(
  onChunk?: (chunk: string) => void, 
  customScene?: string, 
  model?: string, 
  onThinkingChange?: (isThinking: boolean) => void,
  retryCount: number = 0,
  authToken?: string | null
): Promise<string> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add auth token if available
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetchWithTimeout(`${API_BASE}/api/start-story`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        customScene,
        model,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg = errorData.error || `HTTP ${response.status}`;
      const errorDetails = errorData.details ? ` - ${errorData.details}` : '';
      console.error('Start story API error:', errorMsg, errorDetails);
      
      // Handle rate limit error specially
      if (response.status === 429) {
        const message = errorData.message || 'Rate limit exceeded. Please sign in to play unlimited games.';
        throw new Error(`RATE_LIMIT:${message}`);
      }
      
      // Return the error text from the API if available
      if (errorData.text) {
        return errorData.text;
      }
      
      throw new Error(`API error: ${errorMsg}${errorDetails}`);
    }

    // Check if response is streaming
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('text/event-stream')) {
      const result = await handleStreamResponse(response, onChunk, onThinkingChange);
      
      // Retry on stream error
      if (result.text === STREAM_ERROR_MESSAGE && retryCount < MAX_RETRIES) {
        console.log(`Stream error, retrying... (${retryCount + 1}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
        return startStory(onChunk, customScene, model, onThinkingChange, retryCount + 1, authToken);
      }
      
      return result.text;
    }

    // Fallback to non-streaming
    const data = await response.json();
    return data.text || "Welcome to the adventure! (API Error)";
  } catch (error) {
    console.error("Error starting story:", error);
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    
    // Retry on network/stream errors
    if (retryCount < MAX_RETRIES && !errorMsg.includes('API key') && !errorMsg.includes('RATE_LIMIT')) {
      console.log(`Error occurred, retrying... (${retryCount + 1}/${MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
      return startStory(onChunk, customScene, model, onThinkingChange, retryCount + 1, authToken);
    }
    
    if (errorMsg.includes('RATE_LIMIT')) {
      // Extract the message after the prefix
      const message = errorMsg.replace('RATE_LIMIT:', '');
      return `🚫 ${message}`;
    }
    if (errorMsg.includes('timeout')) {
      return "The universe takes too long to load. (Timeout Error)";
    }
    if (errorMsg.includes('API key')) {
      return "The narrator cannot speak without proper credentials. (API Key Error)";
    }
    // Return the actual error message to help with debugging
    return `The universe glitches. ${errorMsg} (API Error)`;
  }
};

export async function continueStory(
  history: Message[], 
  userAction: string, 
  onChunk?: (chunk: string) => void, 
  model?: string,
  winCondition?: WinCondition,
  onThinkingChange?: (isThinking: boolean) => void,
  difficulty?: Difficulty,
  adventurePlan?: AdventurePlan,
  retryCount: number = 0
): Promise<StoryResponse> {
  try {
    const response = await fetchWithTimeout(`${API_BASE}/api/continue-story`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        history,
        userAction,
        model,
        winCondition,
        difficulty,
        adventurePlan,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg = errorData.error || `HTTP ${response.status}`;
      const errorDetails = errorData.details ? ` - ${errorData.details}` : '';
      console.error('Continue story API error:', errorMsg, errorDetails);
      
      // Return the error text from the API if available
      if (errorData.text) {
        return { text: errorData.text };
      }
      
      throw new Error(`API error: ${errorMsg}${errorDetails}`);
    }

    // Check if response is streaming
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('text/event-stream')) {
      const result = await handleStreamResponse(response, onChunk, onThinkingChange);
      
      // Retry on stream error
      if (result.text === STREAM_ERROR_MESSAGE && retryCount < MAX_RETRIES) {
        console.log(`Stream error, retrying... (${retryCount + 1}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
        return continueStory(history, userAction, onChunk, model, winCondition, onThinkingChange, difficulty, adventurePlan, retryCount + 1);
      }
      
      return result;
    }

    // Fallback to non-streaming
    const data = await response.json();
    return { 
      text: data.text || NON_STREAM_ERROR_MESSAGE,
      outcome: data.outcome,
      outcomeReason: data.outcomeReason
    };
  } catch (error) {
    console.error("Error continuing story:", error);
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    
    // Retry on network/stream errors
    if (retryCount < MAX_RETRIES && !errorMsg.includes('API key')) {
      console.log(`Error occurred, retrying... (${retryCount + 1}/${MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
      return continueStory(history, userAction, onChunk, model, winCondition, onThinkingChange, difficulty, adventurePlan, retryCount + 1);
    }
    
    if (errorMsg.includes('timeout')) {
      return { text: "The narrator takes too long to respond. (Timeout Error)" };
    }
    if (errorMsg.includes('API key')) {
      return { text: "The narrator lost their voice. (API Key Error)" };
    }
    // Return the actual error message to help with debugging
    return { text: `The timeline fractures. ${errorMsg} (API Error)` };
  }
};

export async function getPredictions(history: Message[], currentInput: string, model?: string, authToken?: string | null): Promise<string[]> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add auth token if available
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetchWithTimeout(`${API_BASE}/api/get-predictions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        history,
        currentInput,
        model,
      }),
    }, 10000); // Shorter timeout for predictions (10s)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg = errorData.error || `HTTP ${response.status}`;
      const errorDetails = errorData.details ? ` - ${errorData.details}` : '';
      console.error('Predictions API error:', errorMsg, errorDetails);
      
      // Handle rate limit error specially
      if (response.status === 429) {
        // Return the suggestions from the error response if available
        if (errorData.suggestions && Array.isArray(errorData.suggestions)) {
          return errorData.suggestions;
        }
        return ["Login", "for", "unlimited"];
      }
      
      // Return error suggestions if available
      if (errorData.suggestions && Array.isArray(errorData.suggestions)) {
        return errorData.suggestions;
      }
      
      throw new Error(`API error: ${errorMsg}${errorDetails}`);
    }

    const data = await response.json();
    
    // Fallback if API returns unexpected format
    if (!data.suggestions || !Array.isArray(data.suggestions)) {
      console.warn('Invalid predictions format:', data);
      return ["Hit", "Run", "Duck"];
    }

    // Ensure we have exactly 3 suggestions
    const suggestions = data.suggestions.slice(0, 3);
    while (suggestions.length < 3) {
      suggestions.push("...");
    }

    return suggestions;
  } catch (error) {
    console.error("Error generating predictions:", error);
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    if (errorMsg.includes('timeout')) {
      return ["Slow", "Wait", "Hmm"];
    }
    // Show error in suggestions for debugging
    return ["Error", "Check", "Console"];
  }
};

export async function getStoryOptions(
  customScene?: string, 
  model?: string, 
  difficulty: Difficulty = 2, 
  mode: GameMode = 'adventure',
  genre: Genre = 'random',
  dailySeed?: string
): Promise<StoryOption[]> {
  try {
    const response = await fetchWithTimeout(`${API_BASE}/api/story`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customScene,
        model,
        difficulty,
        mode,
        genre,
        dailySeed,
      }),
    }, 20000);

    // Try to parse as JSON
    let data;
    try {
      data = await response.json();
    } catch (jsonError) {
      // If JSON parsing fails, get the raw text for debugging
      const text = await response.text();
      console.error('Failed to parse response as JSON:', text.substring(0, 500));
      throw new Error(`Server returned invalid response (not JSON). Status: ${response.status}. Check server logs for details.`);
    }

    if (!response.ok) {
      // If response is not ok, check if there's an error field
      const errorMsg = data.error || `Server error: HTTP ${response.status}`;
      throw new Error(errorMsg);
    }
    
    if (!data.options || !Array.isArray(data.options)) {
      throw new Error('Invalid response format from server - missing options array');
    }

    return data.options.map((opt: { preview: string; fullPrompt: string; winCondition?: WinCondition; adventurePlan?: AdventurePlan; genre?: Genre }, idx: number) => ({
      id: `option-${idx}`,
      preview: opt.preview,
      fullPrompt: opt.fullPrompt,
      winCondition: opt.winCondition,
      adventurePlan: opt.adventurePlan,
      genre: opt.genre,
    }));
  } catch (error) {
    console.error("Error getting story options:", error);
    // Re-throw the error so the UI can display it
    throw error;
  }
};

// Generate a rewind penalty
export const getRewindPenalty = (): RewindPenalty => {
  const penalties: RewindPenalty[] = [
    { type: 'narrator_sass', message: "The narrator sighs heavily. 'Fine, I'll pretend that didn't happen... but I'm judging you.'" },
    { type: 'narrator_sass', message: "Time rewinds with a disappointed *whoosh*. The universe remembers your cowardice." },
    { type: 'narrator_sass', message: "The narrator reluctantly erases reality. 'This is coming out of your karma, you know.'" },
    { type: 'narrator_sass', message: "*Record scratch* 'Yeah, that's me. You're probably wondering how I got here... again.'" },
    { type: 'narrator_sass', message: "The timeline groans as it's forced to accommodate your indecision." },
    { type: 'chaos_suggestion', description: "Your next suggestion will be extra chaotic" },
    { type: 'word_ban', bannedWord: 'the' },
    { type: 'word_ban', bannedWord: 'I' },
    { type: 'item_loss', item: 'your dignity' },
    { type: 'item_loss', item: 'the narrator\'s respect' },
  ];
  
  return penalties[Math.floor(Math.random() * penalties.length)];
};

// Generate daily challenge seed based on date
export const getDailyChallengeSeed = (date: Date = new Date()): string => {
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
  // Simple hash function for consistent seed
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    const char = dateStr.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `daily-${dateStr}-${Math.abs(hash).toString(36)}`;
};

// Get daily challenge configuration
export const getDailyChallengeConfig = (date: Date = new Date()): { seed: string; genre: Genre; difficulty: Difficulty } => {
  const seed = getDailyChallengeSeed(date);
  const genres: Genre[] = ['fantasy', 'sci-fi', 'horror', 'comedy', 'mystery', 'romance', 'western', 'post-apocalyptic'];
  const difficulties: Difficulty[] = [1, 2, 3, 4];
  
  // Use seed to deterministically pick genre and difficulty
  const seedNum = parseInt(seed.replace(/\D/g, '').slice(0, 8)) || 0;
  const genre = genres[seedNum % genres.length];
  const difficulty = difficulties[(seedNum >> 3) % difficulties.length];
  
  return { seed, genre, difficulty };
};

// Get daily challenge with caching
export async function getDailyChallenge(model?: string): Promise<{ date: string; seed: string; genre: Genre; difficulty: Difficulty; options: StoryOption[] }> {
  try {
    const response = await fetchWithTimeout(`${API_BASE}/api/story?type=daily`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model,
      }),
    }, 30000);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg = errorData.error || `Server error: HTTP ${response.status}`;
      throw new Error(errorMsg);
    }

    const data = await response.json();
    
    if (!data.options || !Array.isArray(data.options)) {
      throw new Error('Invalid response format from server - missing options array');
    }

    return {
      date: data.date,
      seed: data.seed,
      genre: data.genre,
      difficulty: data.difficulty,
      options: data.options.map((opt: { preview: string; fullPrompt: string; winCondition?: WinCondition; adventurePlan?: AdventurePlan; genre?: Genre }, idx: number) => ({
        id: `option-${idx}`,
        preview: opt.preview,
        fullPrompt: opt.fullPrompt,
        winCondition: opt.winCondition,
        adventurePlan: opt.adventurePlan,
        genre: opt.genre,
      })),
    };
  } catch (error) {
    console.error("Error getting daily challenge:", error);
    throw error;
  }
};


// Check if user has hit the rate limit
export async function checkRateLimit(authToken?: string | null): Promise<{
  allowed: boolean;
  remaining: number;
  resetTime: number | null;
  message: string;
  isAuthenticated?: boolean;
  predictions?: {
    allowed: boolean;
    remaining: number;
    resetTime: number | null;
    message: string;
  };
}> {
  try {
    const headers: Record<string, string> = {};
    
    // Add auth token if available
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetchWithTimeout(`${API_BASE}/api/user`, {
      method: 'GET',
      headers,
    }, 5000); // Short timeout for rate limit check

    if (!response.ok) {
      console.error('Rate limit check failed:', response.status);
      // On error, assume allowed (fail open)
      return {
        allowed: true,
        remaining: 2,
        resetTime: null,
        message: 'Unable to verify rate limit',
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error checking rate limit:', error);
    // On error, assume allowed (fail open)
    return {
      allowed: true,
      remaining: 2,
      resetTime: null,
      message: 'Unable to verify rate limit',
    };
  }
};
